import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LegalModalProvider } from './contexts/LegalModalContext';
import { ToastProvider } from './contexts/ToastContext';
import Layout from './components/Common/Layout/Layout';
import ErrorBoundary from './components/Common/ErrorBoundary';
import ScrollToTop from './components/Common/ScrollToTop';
import { errorLogger } from './services/errorLogger';
import Home from './pages/Public/Home';
import LoginHub from './pages/Public/LoginHub';
import RoleLogin from './pages/Public/RoleLogin';
import RegisterMentee from './pages/Public/RegisterMentee';
import RegisterMentor from './pages/Public/RegisterMentor';
import RegisterCompany from './pages/Public/RegisterCompany';
import MentorPanel from './pages/Mentor/Panel/MentorPanel';
import MenteePanel from './pages/Mentee/Panel/MenteePanel';
import CompanyDashboard from './pages/Company/Dashboard';
import CompanyPanel from './pages/Company/Panel/CompanyPanel';
import AdminPanel from './pages/Admin/Panel/AdminPanel';
import Mentees from './pages/Public/Mentees';
import MentorSearch from './pages/Public/MentorSearch';
import MentorDetail from './pages/Public/MentorDetail';
import MenteeDetail from './pages/Public/MenteeDetail';
import Events from './pages/Public/Events';
import BootcampDetail from './pages/Public/BootcampDetail';
import JobPostings from './pages/Public/JobPostings';
import JobDetail from './pages/Public/JobDetail';
import Categories from './pages/Public/Categories';
import PaymentSuccess from './pages/Public/PaymentSuccess';
import PaymentFail from './pages/Public/PaymentFail';
import ForgotPassword from './pages/Public/ForgotPassword';
import ResetPassword from './pages/Public/ResetPassword';
import VerifyEmail from './pages/Public/VerifyEmail';
import MenteeDashboard from './pages/Mentee/Dashboard';
import MentorDashboard from './pages/Mentor/Dashboard';
import LegalInfoPage from './pages/Public/LegalInfoPage';

const RoleDashboard: React.FC = () => {
  const { user, isLoading } = useAuth();
  if (isLoading || !user) return null;
  if (user.role === 'Mentee') return <MenteeDashboard />;
  if (user.role === 'Mentor') return <MentorDashboard />;
  if (user.role === 'Company') return <Navigate to="/company-dashboard" replace />;
  if (user.role === 'Admin') return <Navigate to="/admin/panel" replace />;
  return <Navigate to="/" replace />;
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode; allowedRoles?: string[] }> = ({ children, allowedRoles }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (allowedRoles && allowedRoles.length > 0) {
    const role = user?.role;
    if (!role || !allowedRoles.includes(role)) return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirect if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return !isAuthenticated ? <>{children}</> : <Navigate to="/" replace />;
};

function App() {
  // Global error handler
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      errorLogger.logError(event.error || new Error(event.message), {
        component: 'GlobalErrorHandler',
        action: 'window.onerror',
        additionalData: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      errorLogger.logError(
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason)),
        {
          component: 'GlobalErrorHandler',
          action: 'unhandledrejection',
        }
      );
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <ErrorBoundary>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: '#1e40af',
            borderRadius: 12,
          },
          components: {
            Message: {
              contentPadding: '12px 16px',
              borderRadius: 12,
              fontSize: 14,
            },
          },
        }}
      >
          <ToastProvider>
          <AuthProvider>
            <Router>
              <LegalModalProvider>
              <ScrollToTop />
              <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginHub />
                </PublicRoute>
              }
            />
            <Route
              path="/login/mentee"
              element={
                <PublicRoute>
                  <RoleLogin as="Mentee" />
                </PublicRoute>
              }
            />
            <Route
              path="/login/mentor"
              element={
                <PublicRoute>
                  <RoleLogin as="Mentor" />
                </PublicRoute>
              }
            />
            <Route
              path="/login/company"
              element={
                <PublicRoute>
                  <RoleLogin as="Company" />
                </PublicRoute>
              }
            />
            <Route
              path="/login/admin"
              element={
                <PublicRoute>
                  <RoleLogin as="Admin" />
                </PublicRoute>
              }
            />
            
            <Route 
              path="/forgot-password" 
              element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              } 
            />
            
            <Route 
              path="/reset-password" 
              element={
                <ResetPassword />
              } 
            />
            
            <Route 
              path="/verify-email" 
              element={
                <PublicRoute>
                  <VerifyEmail />
                </PublicRoute>
              } 
            />
            
            <Route 
              path="/register-mentee" 
              element={
                <PublicRoute>
                  <RegisterMentee />
                </PublicRoute>
              } 
            />
            
            <Route 
              path="/register-mentor" 
              element={
                <PublicRoute>
                  <RegisterMentor />
                </PublicRoute>
              } 
            />
            
            <Route 
              path="/register-company" 
              element={
                <PublicRoute>
                  <RegisterCompany />
                </PublicRoute>
              } 
            />
            
            {/* Protected Routes */}
            <Route 
              path="/" 
              element={
                <Layout>
                  <Home />
                </Layout>
              } 
            />
            
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <RoleDashboard />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/profile"
                        element={
                          <ProtectedRoute>
                            <MentorPanel />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/mentee-profile"
                        element={
                          <ProtectedRoute>
                            <MenteePanel />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/company-dashboard"
                        element={
                          <ProtectedRoute>
                            <Layout>
                              <CompanyDashboard />
                            </Layout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/company-panel"
                        element={
                          <ProtectedRoute>
                            <CompanyPanel />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/panel"
                        element={
                          <ProtectedRoute>
                            <AdminPanel />
                          </ProtectedRoute>
                        }
                      />
            
            <Route 
              path="/mentees" 
              element={
                <ProtectedRoute allowedRoles={['Company']}>
                  <Layout>
                    <Mentees />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/mentors" 
              element={
                <Layout>
                  <MentorSearch />
                </Layout>
              } 
            />
            
            <Route 
              path="/mentor-search" 
              element={
                <Layout>
                  <MentorSearch />
                </Layout>
              } 
            />
            
            <Route 
              path="/events" 
              element={
                <Layout>
                  <Events />
                </Layout>
              } 
            />
            <Route 
              path="/categories" 
              element={
                <Layout>
                  <Categories />
                </Layout>
              } 
            />
            <Route
              path="/about"
              element={
                <Layout>
                  <LegalInfoPage section="about" />
                </Layout>
              }
            />
            <Route
              path="/privacy"
              element={
                <Layout>
                  <LegalInfoPage section="privacy" />
                </Layout>
              }
            />
            <Route
              path="/terms"
              element={
                <Layout>
                  <LegalInfoPage section="terms" />
                </Layout>
              }
            />
            <Route 
              path="/job-postings" 
              element={
                <Layout>
                  <JobPostings />
                </Layout>
              } 
            />
            
            <Route 
              path="/job/:id" 
              element={
                <Layout>
                  <JobDetail />
                </Layout>
              } 
            />
            
            <Route 
              path="/bootcamp/:id" 
              element={
                <Layout>
                  <BootcampDetail />
                </Layout>
              } 
            />
            
            <Route 
              path="/mentor/:id" 
              element={
                <Layout>
                  <MentorDetail />
                </Layout>
              } 
            />
            
            <Route 
              path="/mentee/:id" 
              element={
                <ProtectedRoute allowedRoles={['Company', 'Mentor', 'Admin']}>
                  <Layout>
                    <MenteeDetail />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/payment/success" 
              element={
                <PaymentSuccess />
              } 
            />
            
            <Route 
              path="/payment/fail" 
              element={
                <PaymentFail />
              } 
            />
            
            <Route 
              path="/companies" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                      <h1 className="text-3xl font-bold text-gray-900">Şirketler</h1>
                      <p className="mt-2 text-gray-600">Şirketler sayfası yakında eklenecek...</p>
                    </div>
                  </Layout>
                </ProtectedRoute>
              } 
            />
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
              </div>
              </LegalModalProvider>
            </Router>
          </AuthProvider>
          </ToastProvider>
      </ConfigProvider>
    </ErrorBoundary>
  );
}

export default App;